import CN from "classnames";
import Lottie from "lottie-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import loaderAnimation from "../../assets/animations/loader.json";
import mixpanel from "mixpanel-browser";
import { Screen } from "../PodContext";
import Button from "./Button";
import "./Finished.css";
import Footer from "./Footer";
import GoHomeTimer from "./GoHomeTimer";
import Header from "./Header";
import SkipButton from "./SkipButton";
import Text from "./Text";

const CreateAccount = ({
  onClick,
  goToFeedback,
}: {
  onClick: () => void;
  goToFeedback: () => void;
}) => {
  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      <div className="vr-finished-create-account-container">
        <div className="vr-finished-create-account-subcontainer">
          <Text className="text-center">
            If you would like access to support and tools to recharge at any
            time, check out Cabana Live, available on your phone or computer.
          </Text>
          <Text className="text-center">Sign-up takes less than a minute.</Text>
          <Button text="Create account" filled onClick={onClick} />
        </div>
        <div className="vr-finished-create-account-subcontainer">
          <img
            src={"/assets/images/live-group.png"}
            alt="arrow"
            width={335}
            height={216}
          />
          <Button text="Learn more" onClick={() => setOpenModal(true)} />
        </div>

        {openModal && (
          <div className="vr-modal">
            <div className="vr-modal-content">
              <button
                aria-label="Close modal"
                className="vr-close-iframe"
                onClick={() => setOpenModal(false)}
              >
                <img
                  src="/assets/icons/close-circle.svg"
                  alt="Close"
                  width="32"
                  height="32"
                />
              </button>
              <iframe
                src="https://player.vimeo.com/video/951541081?h=0&autoplay=1&muted=1"
                className="vr-iframe"
                title="Learn More Video"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}
      </div>
      <div className="margin-top-20">
        <Button link text="No, thanks." onClick={goToFeedback} />
      </div>
      <Footer />
    </>
  );
};

const CompleteSignUp = ({
  goToFeedback,
  goHome,
}: {
  goToFeedback: () => void;
  goHome: () => void;
}) => {
  const [signedUp, setSignedUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string>("");
  const [error, setError] = useState(false);

  const signup = () => {
    setIsLoading(true);
    mixpanel.track("Signup", { email });
    fetch(
      "https://cabana-prod-service.azurewebsites.net/cabana/public/pod/sign-up",
      {
        method: "POST",
        body: email,
      }
    )
      .then((response) => {
        setIsLoading(false);
        if (response.ok) {
          setSignedUp(true);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setError(true);
        console.error("Signup error:", error);
      });
  };

  const signupOnEnter = (e: any) => {
    if (e.keyCode === 13) signup();
  };

  if (isLoading) {
    return (
      <div className="vr-signup-loading-container">
        <div className="vr-signup-loading-content">
          <Lottie
            animationData={loaderAnimation}
            style={{ width: 200, height: 200 }}
          />
        </div>
      </div>
    );
  }

  if (signedUp) {
    return (
      <div className="vr-signup-success-container">
        <div className="vr-signup-success-content">
          <Text type="subtitle" className="text-center">
            An email has just been sent to you!
          </Text>
          <Text className="text-center">
            📨 Check your inbox to get started on Cabana.
          </Text>
        </div>
        <div className="vr-signup-success-buttons">
          <Button text="Leave feedback" onClick={goToFeedback} filled />
          <Button text="Back to start" onClick={goHome} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vr-signup-success-container">
        <div className="vr-signup-success-content">
          <Text type="subtitle" className="text-center">
            Sorry we couldn’t process your request :(
          </Text>
          <Text className="text-center">
            📨 Please reach out to support@yourcabana.com.
          </Text>
        </div>
        <div className="vr-signup-success-buttons">
          <Button text="Leave feedback" onClick={goToFeedback} filled />
          <Button text="Back to start" onClick={goHome} />
        </div>
      </div>
    );
  }

  return (
    <div className="vr-finished-section">
      <Text type="subtitle" className="text-center">
        Enter your email
      </Text>
      <Text className="text-center text-semibold">
        Your Cabana Live account is free if it’s covered by your sponsoring
        organization.
      </Text>

      <div className={CN("vr-signup-actions", { "fade-out": signedUp })}>
        <div>
          <Text type="small" className="text-semibold">
            Email address
          </Text>
          <input
            className="vr-email-input"
            type="email"
            placeholder="javi@yourorganization.com"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={signupOnEnter}
          />
          <div className="vr-terms-privacy-policy-container">
            <input
              className="vr-terms-checkbox"
              type="checkbox"
              id="terms"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
            />
            <Text type="small">
              I agree to the{" "}
              <span
                aria-hidden="true"
                className="text-underline"
                onClick={() =>
                  setIframeUrl("https://terms-of-service.yourcabana.com")
                }
              >
                Terms of Service
              </span>{" "}
              and{"\n"}
              <span
                aria-hidden="true"
                className="text-underline"
                onClick={() =>
                  setIframeUrl("https://privacy-policy.yourcabana.com")
                }
              >
                Privacy Policy
              </span>
            </Text>
          </div>
        </div>
        <div>
          <Button
            filled
            disabled={email === "" || !isChecked}
            text="Sign me up"
            onClick={signup}
          />
        </div>
      </div>
      {iframeUrl && (
        <div className="vr-modal">
          <div className="vr-modal-content">
            <button
              aria-label="Close modal"
              className="vr-close-iframe"
              onClick={() => setIframeUrl("")}
            >
              <img
                src="/assets/icons/close-circle.svg"
                alt="Close"
                width="32"
                height="32"
              />
            </button>
            <iframe
              src={iframeUrl}
              className="vr-iframe"
              title="Terms of Service and Privacy Policy"
            ></iframe>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

const ThankYou = ({ onClick }: { onClick: () => void }) => {
  return (
    <div className="vr-finished-section">
      <Text className="text-center">
        We would love your help to improve the Cabana Pod. Scan this QR code to
        provide your feedback.
      </Text>
      <img
        style={{ marginTop: 10, marginBottom: 20 }}
        src="/assets/qr-codes/feedback.png"
        className="vr-finished-qr-code"
        alt="qr-code"
      />
      <SkipButton text="Back to start" handleClick={onClick} />
      <Footer />
    </div>
  );
};

const Finished = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);

  const goHome = () => navigate(Screen.HOME);

  const getTitleForStep = (step: number): string => {
    switch (step) {
      case 1:
        return "Optional: Create your Cabana Live account";
      case 2:
        return "";
      default:
        return "Thanks for using the pod!";
    }
  };

  return (
    <div className="image-bg-container">
      <GoHomeTimer />
      <Header
        hasCabanaLogo={currentStep !== 3}
        backToStart
        title={getTitleForStep(currentStep)}
        totalSteps={3}
        progress={currentStep === 2 ? undefined : currentStep + 1}
      />
      <div className="vr-finished-container">
        {currentStep === 1 && (
          <CreateAccount
            onClick={() => setCurrentStep(2)}
            goToFeedback={() => setCurrentStep(3)}
          />
        )}
        {currentStep === 2 && (
          <CompleteSignUp
            goToFeedback={() => setCurrentStep(3)}
            goHome={goHome}
          />
        )}
        {currentStep === 3 && <ThankYou onClick={goHome} />}
      </div>
    </div>
  );
};

export default Finished;
